"use client"

import { useEffect } from "react"
import { Row, Col } from "react-bootstrap"
import { useTranslation } from "../../hooks/use-translation"
import { Link } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { fetchArticles } from "../../redux/slices/blogSlice"
import { formatDate } from "../../utils/timestamp"
import { useAuth } from "../../contexts/auth-context"

export default function BlogPage() {
  const dispatch = useAppDispatch()
  const { user } = useAuth()
  const { articles = [], isLoading } = useAppSelector((state) => state.blog)
  const { t } = useTranslation()
  const isAdmin = user?.role === "admin"

  // Fetch articles when component mounts
  useEffect(() => {
    dispatch(fetchArticles())
  }, [dispatch])

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-light" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4">
      <div
        className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4"
        style={{ height: "64px" }}
      >
        <div className="d-flex align-items-center gap-3">
          <h1 className="text-white fs-2 fw-bold mb-0">{t("blog")}</h1>
        </div>
      </div>

      {articles.length === 0 ? (
        <div
          className="d-flex justify-content-center align-items-center rounded-4 p-5 text-white"
          style={{
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <p className="mb-0 opacity-75">{t("noArticles")}</p>
        </div>
      ) : (
        <Row className="g-4">
          {articles.map((article) => (
            <Col key={article.id} xs={12} xl={6}>
              <div
                className="h-100 rounded-4 overflow-hidden position-relative"
                style={{
                  background: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)"
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.15)"
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)"
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)"
                }}
              >
                <div
                  className="text-center"
                  style={{
                    height: "200px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255, 255, 255, 0.05)",
                  }}
                >
                  <img
                    src={article.thumbnail || "/placeholder.svg?height=200&width=400"}
                    alt={article.title}
                    className="img-fluid rounded"
                    style={{ maxHeight: "100%", maxWidth: "100%" }}
                  />
                </div>
                <div className="p-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-white opacity-75">{formatDate(article.date)}</small>
                  </div>
                  <h5
                    className="text-white fs-5 mb-2"
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {article.title}
                  </h5>
                  <p
                    className="text-white opacity-75 small mb-3"
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {article.preview}
                  </p>
                  <Link to={`/dashboard/blog/${article.id}`}>
                    <button
                      className="btn text-white fw-medium border-0 rounded-pill w-100 py-2"
                      style={{
                        background: "#1a237e",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#283593"
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "#1a237e"
                      }}
                    >
                      {t("readMore")}
                    </button>
                  </Link>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      )}
    </div>
  )
}
